<template>
    <header ref="header" :class="['navigation', `${mobile}-mobile`, { 'menu-active': mobileMenuIsActive }]">
        <div
            class="navigation__wrapper container mx-auto"
            :class="{ 'full-width': blok.FullWidth, 'is-logged-in': isLoggedIn }"
            v-on-click-outside="closeMenu"
        >
            <nuxt-link to="/" class="navigation__logo" external data-cy="navigationLogo" aria-label="home page link">
                <LogoMain v-if="global?.UseDefaultLogo || blok?.UseDefaultLogo" />
                <NuxtImg
                    v-else-if="global?.Logo?.filename"
                    :src="global.Logo.filename"
                    :title="global.Logo.title"
                    :alt="global.Logo.alt"
                    provider="storyblok"
                    format="webp"
                    loading="eager"
                    :height="logoHeight"
                    :width="logoWidth"
                    preload
                />
            </nuxt-link>
            <input
                v-model="mobileMenuIsActive"
                class="navigation__side-menu-checkbox hidden"
                type="checkbox"
                id="side-menu-toggle"
                aria-hidden="true"
                tabindex="-1"
                data-cy="navigationHamburgerCheckbox"
            />
            <label
                class="navigation__side-menu-toggle hamburger lg:hidden"
                for="side-menu-toggle"
                data-cy="openMobileMenu"
                aria-label="Toggle main menu"
                aria-controls="main-navigation"
            >
                <span class="hamburger__line" />
            </label>
            <nav id="main-navigation" class="navigation__nav" data-cy="navigationNav">
                <Menu
                    v-if="blok?.Navigation?.length"
                    :menu="blok?.Navigation"
                    :paths="paths"
                    :class="desktopMenuClasses"
                    @close-menu="closeMenu"
                    @logout="logout"
                    data-cy="desktopMenu"
                />
                <Menu
                    :menu="blok.MobileNavigation"
                    :paths="paths"
                    :class="mobileMenuClasses"
                    @close-menu="closeMenu"
                    @logout="logout"
                    data-cy="mobileMenu"
                />
            </nav>
        </div>
    </header>
</template>

<script setup lang="ts">
import { useModalStore } from '@nuxt-monorepo/base/app/store/modal';
import { useAuthStore } from '@nuxt-monorepo/base/app/store/auth';
import type { IGlobalStoreGlobal, IHeaderConfig } from '@nuxt-monorepo/ui/types/store/global';
import Menu from '@nuxt-monorepo/ui/components/Core/Navigation/Menu.vue';
import { vOnClickOutside } from '@vueuse/components';

const modalStore = useModalStore();
const authStore = useAuthStore();
const {
    blok,
    global,
    logoHeight,
    logoWidth,
    mobile = 'lg',
    paths,
} = defineProps<{
    blok: IHeaderConfig;
    global?: IGlobalStoreGlobal['content'];
    logoHeight?: number;
    logoWidth?: number;
    mobile?: 'md' | 'lg' | 'xl' | 'tablet';
    paths?: Record<string, Record<string, string>>;
}>();

const header: Ref<HTMLElement | null> = ref(null);

onMounted(() => {
    if (!header.value) return;
    header.value.style.setProperty('--header-height', `${header.value.clientHeight || 40}px`);
});

const isLoggedIn = computed(() => {
    return !!(authStore.$state.id && authStore.$state.role && authStore.$state.csrf);
});

const desktopMenuClasses = computed(() => [
    '!hidden',
    {
        'md:!flex': mobile === 'md',
        'tablet:!flex': mobile === 'tablet',
        'lg:!flex': mobile === 'lg',
        'xl:!flex': mobile === 'xl',
    },
    'desktop-menu',
]);
const mobileMenuClasses = computed(() => [
    {
        'md:!hidden': blok.Navigation?.length && mobile === 'md',
        'tablet:!hidden': blok.Navigation?.length && mobile === 'tablet',
        'lg:!hidden': blok.Navigation?.length && mobile === 'lg',
        'xl:!hidden': blok.Navigation?.length && mobile === 'xl',
    },
    'mobile-navigation-menu',
]);

const closeMenu = () => {
    if (!mobileMenuIsActive.value) return;
    mobileMenuIsActive.value = false;
};

const emit = defineEmits(['logout']);
const logout = () => {
    emit('logout');
};

const mobileMenuIsActive = ref(false);

watch(mobileMenuIsActive, (val) => {
    if (val) {
        modalStore.disableScroll();
    } else {
        modalStore.enableScroll();
    }
});

// Provide mobile breakpoint value to use in child component (e.g. DropdownItem)
provide('mobileBreakpoint', mobile);
</script>

<style scoped lang="postcss">
.navigation {
    @apply px-0 overflow-x-clip z-40 shadow-[0_4px_4px_rgba(0,0,0,0.1)];

    &:after {
        @apply content-[''] transition-colors ease-in-out duration-[400ms] bg-transparent;
    }

    &.menu-active:after {
        @apply fixed h-[100dvh] top-0 left-0 w-full bg-black/20 z-30;
    }
    &__wrapper {
        @apply flex justify-between items-center relative px-4;

        &.full-width {
            @apply !max-w-full;
        }
        .md-mobile & {
            @apply md:justify-normal md:gap-4;
        }
        .tablet-mobile & {
            @apply tablet:justify-normal tablet:gap-4;
        }
        .lg-mobile & {
            @apply lg:justify-normal lg:gap-4;
        }
        .xl-mobile & {
            @apply xl:justify-normal xl:gap-4;
        }
    }
    &__logo {
        @apply flex-shrink-0 flex-grow-0 basis-auto;
    }
    &__side-menu-toggle {
        @apply absolute right-0 top-0 z-50;
        @apply flex absolute h-full justify-center items-center right-4 text-primary;

        .menu-active & {
            @apply fixed h-[var(--header-height)];
        }
        .md-mobile & {
            @apply md:hidden;
        }
        .tablet-mobile & {
            @apply tablet:hidden;
        }
        .lg-mobile & {
            @apply lg:hidden;
        }
        .xl-mobile & {
            @apply xl:hidden;
        }
        &:after {
            @apply content-[''] absolute h-full -left-2 -right-2;
        }
    }
    &__nav {
        @apply fixed top-0 right-0 transform translate-x-full transition-transform ease-in-out duration-[400ms] bottom-0;
        @apply w-full max-w-[326px] bg-white z-40 h-[100dvh] px-4 pt-12 pb-4 shadow-[0_4px_4px_rgba(0,0,0,0.1)];
        .md-mobile & {
            @apply md:max-w-none md:transform-none md:relative md:w-auto md:h-full md:flex-shrink md:flex-grow md:basis-auto md:p-0 md:flex md:items-center md:shadow-none;
        }
        .tablet-mobile & {
            @apply tablet:max-w-none tablet:transform-none tablet:relative tablet:w-auto tablet:h-full tablet:flex-shrink tablet:flex-grow tablet:basis-auto tablet:p-0 tablet:flex tablet:items-center tablet:shadow-none;
        }
        .lg-mobile & {
            @apply lg:max-w-none lg:transform-none lg:relative lg:w-auto lg:h-full lg:flex-shrink lg:flex-grow lg:basis-auto lg:p-0 lg:flex lg:items-center lg:shadow-none;
        }
        .xl-mobile & {
            @apply xl:max-w-none xl:transform-none xl:relative xl:w-auto xl:h-full xl:flex-shrink xl:flex-grow xl:basis-auto xl:p-0 xl:flex xl:items-center xl:shadow-none;
        }
    }

    &__side-menu-checkbox:checked ~ &__nav {
        @apply transform translate-x-0;
    }
}

.hamburger {
    cursor: pointer;
    padding: 11px 3px;

    &__line {
        background-color: currentColor;
        display: block;
        height: 2px;
        position: relative;
        width: 18px;
        &:before,
        &:after {
            background-color: currentColor;
            content: '';
            display: block;
            height: 100%;
            position: absolute;
            transition:
                transform 0.2s ease-out,
                top 0.2s ease-out 0.2s;
            width: 100%;
        }
        &:before {
            top: 5px;
        }
        &:after {
            top: -5px;
        }
    }

    .navigation__side-menu-checkbox:checked ~ & {
        .hamburger__line {
            background-color: transparent;

            &:before,
            &:after {
                transition:
                    transform 0.2s ease-out 0.2s,
                    top 0.2s ease-out;
                top: 0;
            }

            &:before {
                transform: rotate(-45deg);
            }
            &:after {
                transform: rotate(45deg);
            }
        }
    }
}
</style>
